import { selectMatchingVideoSource } from '../utils';

/**
 * Videospeler in de stijl van Instagram stories: de video's spelen gedempt
 * automatisch achter elkaar en beginnen daarna weer opnieuw. Links in het beeld
 * tikken gaat terug, rechts verder. Buiten de viewport pauzeert de speler.
 */
export default class VideoStory {
	constructor(el) {
		this.el = el;

		// Alleen slides met een video, dan lopen de indexen van de slides, de
		// video's en de voortgangsbalkjes gelijk
		this.slides = Array.from(this.el.querySelectorAll('[data-video-story-slide]')).filter((slide) =>
			slide.querySelector('[data-video-story-video]')
		);
		this.videos = this.slides.map((slide) => slide.querySelector('[data-video-story-video]'));

		if (!this.videos.length) {
			return;
		}

		this.bars = Array.from(this.el.querySelectorAll('[data-video-story-progress]'));
		this.muteButton = this.el.querySelector('[data-video-story-mute]');
		this.playButton = this.el.querySelector('[data-video-story-play]');
		this.prevButton = this.el.querySelector('[data-video-story-prev]');
		this.nextButton = this.el.querySelector('[data-video-story-next]');

		this.index = 0;
		this.muted = true;
		// Gepauzeerd tot de gebruiker op play tikt, de beginstand hangt af van de
		// voorkeur voor minder beweging (onderaan de constructor)
		this.paused = false;
		this.inView = false;
		this.frame = null;
		this.prepared = new Set();
		this.failed = new Set();

		// Bij één video de browser laten loopen, dat geeft geen zwarte flits
		if (this.videos.length === 1) {
			this.videos[0].loop = true;
		}

		this.onEnded = this.onEnded.bind(this);
		this.onError = this.onError.bind(this);
		this.onPrevClick = this.onPrevClick.bind(this);
		this.onNextClick = this.onNextClick.bind(this);
		this.onMuteClick = this.onMuteClick.bind(this);
		this.onPlayClick = this.onPlayClick.bind(this);
		this.onVisibilityChange = this.onVisibilityChange.bind(this);
		this.onIntersect = this.onIntersect.bind(this);
		this.tick = this.tick.bind(this);

		this.videos.forEach((video) => {
			// Zowel het attribuut als de property, iOS start alleen automatisch
			// als de video echt gedempt is
			video.muted = true;

			video.addEventListener('ended', this.onEnded);
			video.addEventListener('error', this.onError);
		});

		this.prevButton?.addEventListener('click', this.onPrevClick);
		this.nextButton?.addEventListener('click', this.onNextClick);
		this.muteButton?.addEventListener('click', this.onMuteClick);
		this.playButton?.addEventListener('click', this.onPlayClick);

		document.addEventListener('visibilitychange', this.onVisibilityChange);

		this.observer = new IntersectionObserver(this.onIntersect, { threshold: 0.25 });
		this.observer.observe(this.el);

		this.setMuted(true);
		// Wie minder beweging wil begint bij het posterplaatje: de speler loopt
		// pas na een tik op play, en gaat dan gewoon door de video's heen
		this.setPaused(this.prefersReducedMotion());
		this.updateBars();
	}

	/**
	 * @returns {Boolean} of de bezoeker zo weinig beweging als mogelijk wil
	 */
	prefersReducedMotion() {
		if (typeof window.matchMedia !== 'function') {
			return false;
		}

		return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	/**
	 * De video van deze slide klaarzetten: de bron die past bij de
	 * weergavegrootte kiezen en laten inladen
	 *
	 * @param {Number} index
	 */
	prepare(index) {
		const position = this.wrap(index);
		const video = this.videos[position];

		if (!video || this.prepared.has(position)) {
			return;
		}

		this.prepared.add(position);

		video.preload = 'auto';
		selectMatchingVideoSource(video);
	}

	/**
	 * @param {Number} index
	 * @returns {Number} index binnen het aantal video's, ook bij doorlopen
	 */
	wrap(index) {
		const total = this.videos.length;

		return ((index % total) + total) % total;
	}

	/**
	 * @param {Number} index
	 */
	goTo(index) {
		const next = this.wrap(index);
		const current = this.videos[this.index];

		current.pause();
		current.currentTime = 0;

		this.slides[this.index]?.classList.remove('is-active');

		this.index = next;
		this.slides[next]?.classList.add('is-active');

		this.updateBars();
		this.prepare(next);

		this.play();
	}

	next() {
		if (this.videos.length === 1) {
			this.restart();
			return;
		}

		this.goTo(this.index + 1);
	}

	previous() {
		if (this.videos.length === 1) {
			this.restart();
			return;
		}

		this.goTo(this.index - 1);
	}

	restart() {
		const video = this.videos[this.index];

		video.currentTime = 0;
		this.updateBars();
		this.play();
	}

	play() {
		if (this.paused || !this.inView || document.hidden) {
			this.stopTicker();
			return;
		}

		const video = this.videos[this.index];

		this.prepare(this.index);

		video.muted = this.muted;

		const playing = video.play();

		if (playing && typeof playing.catch === 'function') {
			playing.catch(() => {
				// Automatisch spelen mag niet altijd met geluid, in dat geval
				// alsnog gedempt verder
				if (!this.muted) {
					this.setMuted(true);
					video.play().catch(() => {});
				}
			});
		}

		this.startTicker();
	}

	pause() {
		this.videos[this.index].pause();
		this.stopTicker();
	}

	/**
	 * @param {Boolean} muted
	 */
	setMuted(muted) {
		this.muted = muted;

		this.videos.forEach((video) => {
			video.muted = muted;
		});

		this.el.classList.toggle('is-muted', muted);
		this.muteButton?.setAttribute('aria-label', muted ? 'Geluid aanzetten' : 'Geluid uitzetten');
	}

	/**
	 * @param {Boolean} paused
	 */
	setPaused(paused) {
		this.paused = paused;

		this.el.classList.toggle('is-paused', paused);
		this.playButton?.setAttribute('aria-label', paused ? 'Video afspelen' : 'Video pauzeren');

		if (paused) {
			this.pause();
		} else {
			this.play();
		}
	}

	updateBars() {
		this.bars.forEach((bar, index) => {
			// Alles voor de huidige video is afgelopen, alles erna nog niet
			bar.style.width = index < this.index ? '100%' : '0';
		});
	}

	updateProgress() {
		const video = this.videos[this.index];
		const bar = this.bars[this.index];

		if (!bar) {
			return;
		}

		const duration = video.duration;
		const progress = duration ? Math.min(video.currentTime / duration, 1) : 0;

		bar.style.width = `${progress * 100}%`;
	}

	startTicker() {
		if (this.frame) {
			return;
		}

		this.frame = window.requestAnimationFrame(this.tick);
	}

	stopTicker() {
		if (!this.frame) {
			return;
		}

		window.cancelAnimationFrame(this.frame);
		this.frame = null;
	}

	tick() {
		this.frame = window.requestAnimationFrame(this.tick);

		this.updateProgress();

		// De volgende video pas inladen als deze even loopt, dat houdt het
		// laden van de pagina licht en het doorschakelen soepel
		if (this.videos[this.index].currentTime > 1) {
			this.prepare(this.index + 1);
		}
	}

	onEnded() {
		this.failed.clear();

		const bar = this.bars[this.index];

		if (bar) {
			bar.style.width = '100%';
		}

		this.next();
	}

	/**
	 * @param {Event} e
	 */
	onError(e) {
		const index = this.videos.indexOf(e.target);

		if (index !== this.index) {
			return;
		}

		this.failed.add(index);

		// Doorschakelen naar de volgende video, tenzij ze allemaal falen
		if (this.failed.size >= this.videos.length) {
			this.stopTicker();
			return;
		}

		this.next();
	}

	onPrevClick() {
		this.previous();
	}

	onNextClick() {
		this.next();
	}

	onMuteClick() {
		this.setMuted(!this.muted);

		// Voor het geval de video nog niet mocht starten van de browser
		this.play();
	}

	onPlayClick() {
		this.setPaused(!this.paused);
	}

	onVisibilityChange() {
		if (document.hidden) {
			this.pause();
			return;
		}

		this.play();
	}

	/**
	 * @param {IntersectionObserverEntry[]} entries
	 */
	onIntersect(entries) {
		entries.forEach((entry) => {
			this.inView = entry.isIntersecting;

			if (this.inView) {
				this.play();
			} else {
				this.pause();
			}
		});
	}

	destroy() {
		if (!this.videos?.length) {
			return;
		}

		this.stopTicker();
		this.observer?.disconnect();

		document.removeEventListener('visibilitychange', this.onVisibilityChange);

		this.prevButton?.removeEventListener('click', this.onPrevClick);
		this.nextButton?.removeEventListener('click', this.onNextClick);
		this.muteButton?.removeEventListener('click', this.onMuteClick);
		this.playButton?.removeEventListener('click', this.onPlayClick);

		this.videos.forEach((video) => {
			video.removeEventListener('ended', this.onEnded);
			video.removeEventListener('error', this.onError);

			// Het downloaden stoppen als de pagina verlaten wordt: zonder src en
			// zonder sources heeft de video niets meer om te laden
			video.pause();
			video.querySelectorAll('source').forEach((source) => source.remove());
			video.removeAttribute('src');
			video.load();
		});
	}
}
